import { ChevronRightIcon } from "@/components/icons";
import { cn } from "cn";

import type { AgentPart } from "../agent-event.ts";
import type { CallResolution } from "../decisions.ts";
import { CHAT_TEXTS as TEXTS } from "../texts.ts";

type ToolPart = Extract<AgentPart, { type: "tool" }>;

/**
 * Cómo se dice y de qué color es cada estado.
 *
 * Lo que espera una confirmación NO se ha ejecutado: "consultado" ahí diría
 * que ya pasó. Espera en ámbar, como la tarjeta que lo tiene parado, y pasa a
 * lo que pasó de verdad cuando se sabe (R2.1): hecha, o falló.
 */
const look = (
  part: ToolPart,
  resolution: CallResolution,
): { label: string; dot: string; tone?: string } => {
  switch (part.state) {
    case "running":
      return { label: TEXTS.toolCall.running, dot: "animate-pulse bg-warning" };
    case "error":
      return { label: TEXTS.toolCall.error, dot: "bg-destructive" };
    case "done":
      return { label: TEXTS.toolCall.done, dot: "bg-success" };
    case "awaiting":
      switch (resolution.kind) {
        case "waiting":
          return {
            label: TEXTS.toolCall.waiting,
            dot: "bg-warning",
            tone: "text-warning",
          };
        case "approved":
          return { label: TEXTS.toolCall.approved, dot: "bg-success" };
        case "done":
          return { label: TEXTS.toolCall.executed, dot: "bg-success" };
        case "failed":
          return {
            label: TEXTS.toolCall.failedToRun,
            dot: "bg-destructive",
            tone: "text-destructive",
          };
        case "rejected":
          return { label: TEXTS.toolCall.rejected, dot: "bg-muted-foreground" };
        case "expired":
          return { label: TEXTS.toolCall.expired, dot: "bg-muted-foreground" };
        case "resolved":
          return {
            label: TEXTS.toolCall.resolved,
            dot: "bg-muted-foreground",
          };
      }
  }
};

/**
 * Traza de una llamada a herramienta. No es decoración: si el agente consulta
 * datos, el usuario tiene que poder ver QUÉ consultó para poder desconfiar del
 * resultado cuando toque.
 */
export function ToolCall({
  part,
  resolution,
}: {
  part: ToolPart;
  /** En qué quedó su confirmación. Solo cuenta si `awaiting`. */
  resolution: CallResolution;
}) {
  const { label, dot, tone } = look(part, resolution);

  return (
    <details className="group rounded-md border bg-secondary/40 text-sm">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2">
        <ChevronRightIcon className="size-3.5 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
        <span
          aria-hidden="true"
          className={cn("size-1.5 shrink-0 rounded-full", dot)}
        />
        <code className="truncate">{part.toolName}</code>
        <span className={cn("ml-auto text-muted-foreground", tone)}>
          {label}
        </span>
      </summary>

      <div className="space-y-3 border-t px-3 py-2">
        {part.input !== undefined && (
          <Section
            label={TEXTS.toolCall.input}
            body={JSON.stringify(part.input, null, 2)}
          />
        )}
        {part.state === "error" && part.errorText !== undefined && (
          <Section label={TEXTS.toolCall.errorSection} body={part.errorText} />
        )}
        {resolution.kind === "failed" && resolution.error !== null && (
          <Section label={TEXTS.toolCall.whyFailed} body={resolution.error} />
        )}
        {/* Lo que espera confirmación no tiene resultado: su "salida" es el
            sobre que se le dio al modelo para que esperase. */}
        {part.state === "done" && part.output !== undefined && (
          <Section
            label={TEXTS.toolCall.result}
            body={JSON.stringify(part.output, null, 2)}
          />
        )}
      </div>
    </details>
  );
}

function Section({ label, body }: { label: string; body: string }) {
  return (
    <div className="space-y-1">
      <span className="text-xs tracking-wide text-muted-foreground uppercase">
        {label}
      </span>
      {/* Scroll propio: una entrada larga no puede estirar la conversación. */}
      <pre className="max-h-64 overflow-auto rounded-sm bg-background p-2 text-xs">
        {body}
      </pre>
    </div>
  );
}
